import { useState, useRef } from 'react';
import { Play, Pause, Volume2, Smartphone, CheckCircle, XCircle, Info } from 'lucide-react';
import { 
  isIOSDevice, 
  checkIOSMediaSupport, 
  getIOSMediaPolicy,
  prepareAudioForIOS,
  tryPlayAudioIOS 
} from '../utils/iosHelpers';

const MediaTest = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [currentTest, setCurrentTest] = useState(null);
  const audioRef = useRef(null);

  const isIOS = isIOSDevice();
  const policy = getIOSMediaPolicy();

  // Test ses dosyaları
  const testAudios = [
    { name: 'MP3 Test', url: '/media/sound/bgm/bgm_1.mp3', format: 'mp3' },
    { name: 'MP3 Test 2', url: '/media/sound/bgm/bgm_2.mp3', format: 'mp3' }
  ];

  const runMediaTest = async (audio) => {
    setCurrentTest(audio.name);
    const results = { ...testResults };
    
    try {
      // Format kontrolü
      const formatCheck = checkIOSMediaSupport(audio.url);
      results[audio.name] = {
        formatSupported: formatCheck.supported,
        format: formatCheck.format,
        recommendation: formatCheck.recommendation
      };

      // Audio element test
      if (audioRef.current) {
        audioRef.current.src = audio.url;
        
        if (isIOS) {
          prepareAudioForIOS(audioRef.current);
          await tryPlayAudioIOS(audioRef.current);
        } else {
          await audioRef.current.play();
        }
        
        results[audio.name].playbackTest = 'success';
        setIsPlaying(true);
        
        // 2 saniye sonra durdur
        setTimeout(() => {
          audioRef.current?.pause();
          setIsPlaying(false);
        }, 2000);
        
      }
    } catch (error) {
      results[audio.name].playbackTest = 'failed';
      results[audio.name].error = error.message;
    }
    
    setTestResults(results);
    setCurrentTest(null);
  };

  const runAllTests = async () => {
    for (const audio of testAudios) {
      await runMediaTest(audio);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Smartphone className="w-6 h-6 mr-2" />
            iOS Medya Uyumluluk Testi
          </h1>

          {/* Cihaz Bilgisi */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h2 className="text-lg font-semibold text-blue-900 mb-2">Cihaz Bilgisi</h2>
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <span className="font-medium w-32">Platform:</span>
                <span>{isIOS ? 'iOS Safari' : 'Diğer Tarayıcı'}</span>
                {isIOS ? (
                  <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                ) : (
                  <Info className="w-4 h-4 text-blue-500 ml-2" />
                )}
              </div>
              <div className="flex items-center">
                <span className="font-medium w-32">User Agent:</span>
                <span className="text-xs">{navigator.userAgent}</span>
              </div>
              <div className="flex items-center">
                <span className="font-medium w-32">HTTPS:</span>
                <span>{location.protocol === 'https:' ? 'Aktif' : 'Pasif'}</span>
                {location.protocol === 'https:' ? (
                  <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-500 ml-2" />
                )}
              </div>
            </div>
          </div>

          {/* iOS Politikası */}
          {isIOS && policy && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
              <h2 className="text-lg font-semibold text-yellow-900 mb-2">iOS Medya Politikası</h2>
              <div className="space-y-2 text-sm text-yellow-800">
                <div>✅ Desteklenen Audio Formatları: {policy.supportedAudioFormats.join(', ')}</div>
                <div>✅ Desteklenen Video Formatları: {policy.supportedVideoFormats.join(', ')}</div>
                <div>⚠️ Kullanıcı etkileşimi gerekli: {policy.requiresUserInteraction ? 'Evet' : 'Hayır'}</div>
                <div>🔒 HTTPS gerekli: {policy.requiresHTTPS ? 'Evet' : 'Hayır'}</div>
              </div>
            </div>
          )}

          {/* Test Butonları */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Medya Testleri</h2>
            <div className="flex flex-wrap gap-3 mb-4">
              <button
                onClick={runAllTests}
                disabled={currentTest}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {currentTest ? 'Test Çalışıyor...' : 'Tüm Testleri Çalıştır'}
              </button>
              
              {testAudios.map((audio) => (
                <button
                  key={audio.name}
                  onClick={() => runMediaTest(audio)}
                  disabled={currentTest}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Play className="w-4 h-4 mr-1" />
                  {audio.name}
                </button>
              ))}
            </div>
          </div>

          {/* Test Sonuçları */}
          {Object.keys(testResults).length > 0 && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Test Sonuçları</h2>
              <div className="space-y-4">
                {Object.entries(testResults).map(([testName, result]) => (
                  <div key={testName} className="p-4 border rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">{testName}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="w-32">Format Desteği:</span>
                        {result.formatSupported ? (
                          <span className="flex items-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Destekleniyor ({result.format})
                          </span>
                        ) : (
                          <span className="flex items-center text-red-600">
                            <XCircle className="w-4 h-4 mr-1" />
                            Desteklenmiyor ({result.format})
                          </span>
                        )}
                      </div>
                      
                      {result.playbackTest && (
                        <div className="flex items-center">
                          <span className="w-32">Oynatma Testi:</span>
                          {result.playbackTest === 'success' ? (
                            <span className="flex items-center text-green-600">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Başarılı
                            </span>
                          ) : (
                            <span className="flex items-center text-red-600">
                              <XCircle className="w-4 h-4 mr-1" />
                              Başarısız
                            </span>
                          )}
                        </div>
                      )}
                      
                      {result.error && (
                        <div className="text-red-600 text-xs">
                          Hata: {result.error}
                        </div>
                      )}
                      
                      {result.recommendation && (
                        <div className="text-yellow-600 text-xs">
                          Öneri: {result.recommendation}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gizli Audio Element */}
          <audio
            ref={audioRef}
            style={{ display: 'none' }}
            playsInline
            crossOrigin="anonymous"
          />

          {/* Yardım */}
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Kullanım Talimatları</h2>
            <div className="text-sm text-gray-600 space-y-1">
              <div>1. iOS cihazınızda bu sayfayı HTTPS ile açın</div>
              <div>2. "Tüm Testleri Çalıştır" butonuna basın</div>
              <div>3. Test sonuçlarını kontrol edin</div>
              <div>4. Herhangi bir sorun varsa konsolu (F12) kontrol edin</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaTest;
